/**
 * Inline form (not a modal) to queue a new keyword run. Sits at the top
 * of the project Keywords tab so the user can fire off a run without
 * leaving the page.
 *
 * Phase 1 smart-seed: default flow is zero-typing. The form pre-fetches the
 * project so it can send a `{ type: 'domain', value: project.domain }` seed
 * when the user leaves the topics field blank, and toggles `smart_mode` on
 * via the "Use AI seed enrichment" checkbox (default checked). Turning the
 * checkbox off restores the legacy length-based heuristic verbatim.
 */
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import type { CreateKeywordRunRequest, KeywordRun } from '@seo/types';
import { Button } from '@/components/ui/button';
import { TextField, FormField } from '@/components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Section, SectionHeader, SectionTitle } from '@/components/ui/section';
import { Spinner } from '@/components/spinner';
import { summarizeApiError } from '@/lib/api';
import { useCreateKeywordRun } from '@/features/keywords/use-create-keyword-run';
import { useProject } from '@/features/projects/use-project';

const COUNTRY_OPTIONS = [
  { code: 'US', label: 'United States' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'IN', label: 'India' },
  { code: 'DE', label: 'Germany' },
  { code: 'CA', label: 'Canada' },
  { code: 'AU', label: 'Australia' },
] as const;

const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
  { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
] as const;

const KeywordRunFormSchema = z
  .object({
    seed: z
      .string()
      .trim()
      .max(2000, 'Too long — keep it under 2000 characters'),
    smart_mode: z.boolean().default(true),
    country_code: z.string().length(2),
    language: z.string().regex(/^[a-z]{2,3}$/),
  })
  .refine((v) => v.smart_mode || v.seed.length >= 2, {
    path: ['seed'],
    message: 'Provide a seed keyword, topic, or description',
  });
type KeywordRunFormValues = z.infer<typeof KeywordRunFormSchema>;

export interface KeywordRunCreateFormProps {
  projectId: string;
  onCreated?: (run: KeywordRun) => void;
  title?: string;
}

/**
 * Best-effort URL coercion. `Project.domain` is validated as HttpUrl by the
 * backend schema so this should always succeed — kept as a defensive guard
 * against future schema drift or seed values that leak into the field.
 */
function toValidUrl(value: string | undefined): string | null {
  if (!value) return null;
  try {
    // Throws on malformed input.
    return new URL(value).toString();
  } catch {
    return null;
  }
}

export function KeywordRunCreateForm({
  projectId,
  onCreated,
  title = 'Start a keyword run',
}: KeywordRunCreateFormProps): JSX.Element {
  const create = useCreateKeywordRun();
  const project = useProject(projectId);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<KeywordRunFormValues>({
    resolver: zodResolver(KeywordRunFormSchema),
    defaultValues: {
      seed: '',
      smart_mode: true,
      country_code: 'US',
      language: 'en',
    },
  });

  const country = watch('country_code');
  const language = watch('language');

  const projectDomain = toValidUrl(project.data?.domain);

  return (
    <Section>
      <form
        className="space-y-4"
        noValidate
        onSubmit={handleSubmit(async (values) => {
          const text = values.seed.trim();

          // Build the seed. When Smart mode is ON and the text is empty we
          // send the project's domain so the backend can crawl+enrich; when
          // OFF we preserve today's length-based heuristic exactly.
          const seed: CreateKeywordRunRequest['seed'] = (() => {
            if (text.length === 0) {
              if (values.smart_mode) {
                if (projectDomain) {
                  return { type: 'domain', value: projectDomain };
                }
                // Project not loaded yet (or unusable domain). Fall back to
                // a lone topics placeholder so the API call doesn't fail —
                // the enrichment step will still crawl from the project
                // record on the backend.
                return { type: 'topics', value: ['keyword research'] };
              }
              // Smart mode OFF + empty seed is blocked by the Zod refine,
              // so this branch is unreachable — return a safe default in
              // case validation is ever bypassed.
              return { type: 'topics', value: ['keyword research'] };
            }

            if (text.length >= 20) {
              return { type: 'description', value: text };
            }

            if (values.smart_mode) {
              // Comma-separated list becomes multiple topics; single term
              // when the user typed one word or phrase.
              const topics = text
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean);
              return {
                type: 'topics',
                value: topics.length > 0 ? topics : [text],
              };
            }

            // Legacy path: single-token topics seed, unchanged from before.
            return { type: 'topics', value: [text] };
          })();

          // Warn the user (but still submit) when we had to fall back to
          // the placeholder because the project hadn't loaded yet.
          if (
            values.smart_mode &&
            text.length === 0 &&
            !projectDomain
          ) {
            toast.warning('Project still loading', {
              description:
                'We queued a run without the site context — retry once the page finishes loading for the best results.',
            });
          }

          const payload: CreateKeywordRunRequest = {
            project_id: projectId,
            seed,
            smart_mode: values.smart_mode,
            country_code: values.country_code.toUpperCase(),
            language: values.language.toLowerCase(),
            // reuse_audit defaults to true on the backend — omit unless we
            // surface a UI control for it.
            reuse_audit: true,
          };

          try {
            const run = await create.mutateAsync(payload);
            toast.success('Keyword run queued', {
              description:
                'Expansion, scoring and clustering will run in the background.',
            });
            reset({
              seed: '',
              smart_mode: values.smart_mode,
              country_code: values.country_code,
              language: values.language,
            });
            onCreated?.(run);
          } catch (e) {
            const summary = summarizeApiError(e, {
              fallbackTitle: 'Could not start the keyword run',
            });
            toast.error(summary.title, { description: summary.description });
          }
        })}
      >
        <SectionHeader>
          <SectionTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            {title}
          </SectionTitle>
        </SectionHeader>

        {/*
          Subtitle: names the site we're researching so users understand the
          zero-typing flow. Hidden on error — the form still works without it.
        */}
        {project.isLoading ? (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Spinner className="h-3 w-3" />
            Loading project…
          </p>
        ) : project.data ? (
          <p className="text-xs text-muted-foreground">
            We'll research keywords for{' '}
            <span className="font-medium text-foreground">
              {project.data.domain}
            </span>
            . Add topics below to bias the results, or leave blank.
          </p>
        ) : null}

        <TextField
          label="Additional topics (optional)"
          placeholder="e.g. domain investing, expired domains"
          error={errors.seed?.message}
          hint="Comma-separated topics, or a short description (20+ chars treated as a brief)."
          {...register('seed')}
        />

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            {...register('smart_mode')}
          />
          <span className="space-y-0.5">
            <span className="block font-medium">Use AI seed enrichment</span>
            <span className="block text-xs text-muted-foreground">
              Reads your site + generates realistic search queries. Turn off
              for the legacy topic-only flow.
            </span>
          </span>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Country">
            <Select
              value={country}
              onValueChange={(v) =>
                setValue('country_code', v, { shouldDirty: true })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRY_OPTIONS.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.code} — {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Language">
            <Select
              value={language}
              onValueChange={(v) =>
                setValue('language', v, { shouldDirty: true })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGE_OPTIONS.map((l) => (
                  <SelectItem key={l.code} value={l.code}>
                    {l.code} — {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? <Spinner className="mr-2" /> : null}
            Start run
          </Button>
        </div>
      </form>
    </Section>
  );
}
